import React from 'react';
import { AlertTriangleIcon } from 'lucide-react';
import { ActingDetailQueryResponse } from '@area-restrita/api-client/model/actingDetailQueryResponse';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import {
  formatRouteCount,
  formatRouteDistance,
  formatRoutePercentage,
} from './actingDetailsPresentation';

interface IProps {
  routeComparison: ActingDetailQueryResponse['routeComparison'];
}

const ActingRouteAnalysisCard: React.FC<IProps> = ({ routeComparison }) => {
  const { metrics, traceSummary } = routeComparison;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Análise do percurso</CardTitle>
        <CardDescription>
          Indicadores calculados a partir dos segmentos de GPS confiáveis
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {routeComparison.status === 'calculation_failed' && (
          <Alert>
            <AlertTriangleIcon />
            <AlertTitle>Distâncias indisponíveis</AlertTitle>
            <AlertDescription>
              Não foi possível calcular as métricas de distância. As geometrias
              e contagens continuam disponíveis.
            </AlertDescription>
          </Alert>
        )}
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt className="text-sm text-muted-foreground">
              Distância executada
            </dt>
            <dd className="text-lg font-semibold">
              {formatRouteDistance(metrics?.executedDistanceMeters)}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">
              Distância fora da rota
            </dt>
            <dd className="text-lg font-semibold">
              {formatRouteDistance(metrics?.offRouteDistanceMeters)}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">
              Pontos fora da rota
            </dt>
            <dd className="text-lg font-semibold">
              {formatRouteCount(metrics?.offRoutePointCount)} (
              {formatRoutePercentage(metrics?.offRoutePointPercentage)})
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">
              Pontos aproveitados
            </dt>
            <dd className="text-lg font-semibold">
              {formatRouteCount(traceSummary.usedPoints)} de{' '}
              {formatRouteCount(traceSummary.totalRecordedPoints)}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">Pontos ignorados</dt>
            <dd className="text-lg font-semibold">
              {formatRouteCount(traceSummary.ignoredPoints)}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-muted-foreground">Descontinuidades</dt>
            <dd className="text-lg font-semibold">
              {formatRouteCount(traceSummary.discontinuityCount)}
            </dd>
          </div>
        </dl>
        <p className="text-sm text-muted-foreground">
          Tolerância aplicada:{' '}
          {metrics
            ? `${formatRouteCount(metrics.toleranceMeters)} m`
            : 'Indisponível'}
        </p>
      </CardContent>
    </Card>
  );
};

export default ActingRouteAnalysisCard;
